import { SignInSchema, useSignInForm } from "@/hooks/useSignInForm";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { SocialSignin } from "./SocialSignin";
import { AuthenticationScreenProps, AuthenticationType } from "./types";
import { delay } from "@/libs/utils";
import { useAuthValue } from "@/contexts/contextAuth";
import { dealNowDomain } from "@/libs/clientConfig";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Zap } from "lucide-react";

export const Signin = ({ changeScreen }: AuthenticationScreenProps) => {
  const secondaryAuthButtonClass =
    "w-full border border-[#808080] bg-white text-[#212529] hover:bg-[#F7F9FC]";
  const form = useSignInForm();
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = form;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showEmailPasswordForm, setShowEmailPasswordForm] =
    useState<boolean>(false);
  const [isMagicLinkSent, setIsMagicLinkSent] = useState<boolean>(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSubmitting = useRef(false);

  const {
    onLogin,
    onMagicLink,
    setMessage,
    setType,
    setIsShow,
    showSigninToDealNow,
  } = useAuthValue();
  const email = watch("email");
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim() || "");
  const showMagicLinkForm = !showEmailPasswordForm && !isMagicLinkSent;

  const validateEmailField = useCallback(() => {
    const currentEmail = getValues("email")?.trim() || "";
    if (!currentEmail) {
      setError("email", { type: "manual", message: "Email is required" });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentEmail)) {
      setError("email", { type: "manual", message: "Invalid email" });
      return false;
    }
    clearErrors("email");
    return true;
  }, [clearErrors, getValues, setError]);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    if (resendTimer.current) {
      clearInterval(resendTimer.current);
    }
    resendTimer.current = setInterval(() => {
      setResendCooldown((current) => {
        if (current <= 1) {
          if (resendTimer.current) {
            clearInterval(resendTimer.current);
            resendTimer.current = null;
          }
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (resendTimer.current) {
        clearInterval(resendTimer.current);
      }
    };
  }, []);

  const sendMagicLink = useCallback(async () => {
    if (isSendingMagicLink || resendCooldown > 0 || !validateEmailField()) {
      return;
    }

    setIsSendingMagicLink(true);
    try {
      await onMagicLink(getValues("email").trim().toLowerCase());
      setIsMagicLinkSent(true);
      startResendCooldown();
      setType("success");
      setMessage("Check your email for your sign-in link");
      setIsShow(true);
    } catch {
      //
    } finally {
      setIsSendingMagicLink(false);
    }
  }, [
    getValues,
    isSendingMagicLink,
    onMagicLink,
    resendCooldown,
    setIsShow,
    setMessage,
    setType,
    startResendCooldown,
    validateEmailField,
  ]);

  const showPasswordSignIn = useCallback(() => {
    if (validateEmailField()) {
      setShowEmailPasswordForm(true);
    }
  }, [validateEmailField]);

  const onSubmit = useCallback(
    handleSubmit(async (data: SignInSchema) => {
      if (isSubmitting.current) return;

      isSubmitting.current = true;
      try {
        const successUrl = await onLogin(data.email, data.password);
        setType("success");
        setMessage("Login Successful");
        setIsShow(true);
        await delay(500);
        window.location.href = successUrl;
      } catch {
        isSubmitting.current = false;
      }
    }),
    [handleSubmit, onLogin, setIsShow, setMessage, setType]
  );

  return (
    <form onSubmit={onSubmit} className="w-full">
      <div className="md:min-h-[410px]">
        <h2 className="text-3xl text-[#212529] font-bold mb-7">Sign in</h2>

        {showMagicLinkForm && (
          <div>
            <SocialSignin action="signin" />
            <div className="flex items-center w-full my-4">
              <div className="flex-grow border-t border-[#C6C6C6]" />
              <span className="px-4 text-[#6B7280] text-sm">or</span>
              <div className="flex-grow border-t border-[#C6C6C6]" />
            </div>
            <div>
              <div className="relative">
                <input
                  type="email"
                  className="border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 pr-10 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                  placeholder="Enter your email"
                  {...register("email")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      showPasswordSignIn();
                    }
                  }}
                />
                {isEmailValid && (
                  <CheckCircle2
                    aria-hidden="true"
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#00A878]"
                  />
                )}
              </div>
              {errors?.email && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors.email.message}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={showPasswordSignIn}
              disabled={!isEmailValid}
              className="w-full bg-[#00b3de] hover:opacity-80 text-white mt-5"
            >
              Sign in with password
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={sendMagicLink}
              disabled={
                !isEmailValid || isSendingMagicLink || resendCooldown > 0
              }
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              <Zap aria-hidden="true" className="mr-2 h-4 w-4" />
              {isSendingMagicLink
                ? "Sending..."
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Send me a sign-in link instead"}
            </Button>
          </div>
        )}

        {isMagicLinkSent && !showEmailPasswordForm && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F7F2]">
              <CheckCircle2
                aria-hidden="true"
                className="h-5 w-5 text-[#00A878]"
              />
            </div>
            <h3 className="text-xl font-bold text-[#212529]">
              Check your email
            </h3>
            <p className="mt-2 text-sm text-[#4B5563]">
              We sent a sign-in link to{" "}
              <span className="font-semibold text-[#212529]">
                {getValues("email")}
              </span>
              .
            </p>
            <Button
              type="button"
              variant={null}
              onClick={sendMagicLink}
              disabled={isSendingMagicLink || resendCooldown > 0}
              className={`${secondaryAuthButtonClass} mt-5`}
            >
              {isSendingMagicLink
                ? "Resending..."
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend magic link"}
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={() => {
                setIsMagicLinkSent(false);
                setShowEmailPasswordForm(false);
              }}
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              Use a different email
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={() => setShowEmailPasswordForm(true)}
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              Sign in with password instead
            </Button>
          </div>
        )}

        {showEmailPasswordForm && (
          <div>
            <div>
              <input
                type="email"
                className="border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                placeholder="Enter your email"
                {...register("email")}
              />
              {errors?.email && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors.email.message}
                </div>
              )}
            </div>
            <div className="relative mt-4">
              <input
                type={showPassword ? "text" : "password"}
                className="bg-no-repeat bg-[center_right_0.5rem] border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                placeholder="Enter your password"
                {...register("password")}
              />
              {errors?.password && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors?.password?.message}
                </div>
              )}
              {showPassword ? (
                <Image
                  className="absolute top-3 right-3 cursor-pointer"
                  width={17}
                  height={15}
                  src={`/assets/eyeCrossedOut.svg`}
                  alt="eye"
                  onClick={() => setShowPassword(!showPassword)}
                />
              ) : (
                <Image
                  className="absolute top-4 right-3 cursor-pointer"
                  width={15}
                  height={11}
                  src={`/assets/eye.svg`}
                  alt="eye"
                  onClick={() => setShowPassword(!showPassword)}
                />
              )}
            </div>
            <Button
              type="submit"
              disabled={isSubmitting.current}
              className="w-full bg-[#00b3de] hover:opacity-80 text-white mt-5"
            >
              Sign in
            </Button>
            {showSigninToDealNow && (
              <div className="text-xs text-[#ed0a0a] py-2">
                Please use{" "}
                <a
                  className="underline"
                  href={`https://${dealNowDomain}/?account=login`}
                >
                  {dealNowDomain}
                </a>{" "}
                to sign in.
              </div>
            )}
            <div className="mt-4">
              <span
                className="text-[#00b3de] font-medium cursor-pointer"
                role="link"
                onClick={() => changeScreen(AuthenticationType.ForgotPassword)}
              >
                Forgot Password?
              </span>
            </div>
          </div>
        )}

        {showEmailPasswordForm && (
          <div className="text-lg text-[#212529] font-medium py-2 text-center mt-12">
            New to PrivateAuto?{" "}
            <span
              className="text-[#00b3de] underline cursor-pointer"
              role="link"
              onClick={() => changeScreen(AuthenticationType.Signup)}
            >
              Create an account
            </span>
          </div>
        )}
      </div>
    </form>
  );
};
